import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled, { createGlobalStyle } from 'styled-components';
import { api } from '../services/api';

const PublicPageStyles = createGlobalStyle`
  .public-page * { margin: 0; padding: 0; box-sizing: border-box; }
  .public-page {
    --primary: #00d4aa;
    --primary-dark: #00b894;
    --primary-glow: rgba(0, 212, 170, 0.25);
    --bg-dark: #0a0e17;
    --bg-card: #101826;
    --bg-card-soft: #121c2b;
    --text-light: #f8fafc;
    --text-muted: #94a3b8;
    --gradient-primary: linear-gradient(135deg, #00d4aa 0%, #00b894 55%, #0891b2 100%);

    font-family: 'Poppins', sans-serif;
    background: radial-gradient(circle at top left, rgba(0, 212, 170, 0.08) 0%, transparent 45%),
      radial-gradient(circle at bottom right, rgba(8, 145, 178, 0.08) 0%, transparent 50%),
      var(--bg-dark);
    color: var(--text-light);
    line-height: 1.7;
    min-height: 100vh;
  }

  .public-page a { color: inherit; text-decoration: none; }
`;

const Shell = styled.div`
  position: relative;
`;

const Glow = styled.div<{ $variant: 'top' | 'bottom' }>`
  position: fixed;
  width: 520px;
  height: 520px;
  border-radius: 50%;
  filter: blur(160px);
  opacity: 0.18;
  pointer-events: none;
  z-index: 0;
  top: ${props => props.$variant === 'top' ? '-240px' : 'auto'};
  bottom: ${props => props.$variant === 'bottom' ? '-260px' : 'auto'};
  left: ${props => props.$variant === 'top' ? '-200px' : 'auto'};
  right: ${props => props.$variant === 'bottom' ? '-220px' : 'auto'};
  background: ${props => props.$variant === 'top' ? 'var(--primary)' : '#0891b2'};
`;

const Header = styled.header`
  position: sticky;
  top: 0;
  z-index: 10;
  backdrop-filter: blur(10px);
  background: rgba(10, 14, 23, 0.9);
  border-bottom: 1px solid rgba(0, 212, 170, 0.12);
`;

const HeaderInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 900px) {
    flex-wrap: wrap;
    justify-content: center;
  }
`;

const Logo = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  background: none;
  border: none;
  cursor: pointer;

  img { height: 52px; }
`;

const Nav = styled.nav`
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;

  a {
    color: var(--text-muted);
    font-size: 0.95rem;
    transition: color 0.2s ease;
  }

  a:hover {
    color: var(--primary);
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;

  @media (max-width: 900px) {
    display: none;
  }
`;

const MobileToggle = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 12px;
  border: 1px solid rgba(0, 212, 170, 0.35);
  background: rgba(0, 212, 170, 0.08);
  color: var(--text-light);
  cursor: pointer;

  span {
    display: block;
    width: 20px;
    height: 2px;
    background: currentColor;
    border-radius: 999px;
    position: relative;
  }

  span::before,
  span::after {
    content: '';
    position: absolute;
    left: 0;
    width: 20px;
    height: 2px;
    background: currentColor;
    border-radius: 999px;
  }

  span::before {
    top: -6px;
  }

  span::after {
    top: 6px;
  }

  @media (max-width: 900px) {
    display: flex;
  }
`;

const MobileMenu = styled.div<{ $open: boolean }>`
  display: none;

  @media (max-width: 900px) {
    display: block;
    width: 100%;
    max-height: ${props => props.$open ? '360px' : '0'};
    opacity: ${props => props.$open ? 1 : 0};
    transform: translateY(${props => props.$open ? '0' : '-6px'});
    overflow: hidden;
    transition: max-height 0.3s ease, opacity 0.2s ease, transform 0.2s ease;
    border-top: 1px solid rgba(0, 212, 170, 0.12);
    background: rgba(10, 14, 23, 0.97);
  }
`;

const MobileMenuInner = styled.div`
  padding: 14px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  a {
    color: var(--text-muted);
    font-size: 1rem;
  }

  a:hover {
    color: var(--primary);
  }
`;

const MobileActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;

  button {
    width: 100%;
  }
`;

const SecondaryButton = styled.button`
  padding: 10px 22px;
  border-radius: 999px;
  border: 1px solid var(--primary);
  background: transparent;
  color: var(--primary);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--primary);
    color: var(--bg-dark);
  }
`;

const PrimaryButton = styled.button`
  padding: 10px 24px;
  border-radius: 999px;
  border: none;
  background: var(--gradient-primary);
  color: #0a0e17;
  font-weight: 700;
  cursor: pointer;
  box-shadow: 0 10px 25px var(--primary-glow);
  transition: transform 0.2s ease;

  &:hover {
    transform: translateY(-2px);
  }
`;

const Hero = styled.section`
  padding: 80px 20px 40px;
`;

const HeroInner = styled.div`
  max-width: 980px;
  margin: 0 auto;
  text-align: center;
`;

const Eyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(0, 212, 170, 0.12);
  border: 1px solid rgba(0, 212, 170, 0.4);
  color: var(--primary);
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 600;
`;

const Title = styled.h1`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  margin: 18px 0 12px;
`;

const Subtitle = styled.p`
  color: var(--text-muted);
  font-size: 1.05rem;
`;

const Content = styled.main`
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 20px 80px;
  position: relative;
  z-index: 1;
`;

const Footer = styled.footer`
  border-top: 1px solid rgba(0, 212, 170, 0.1);
  padding: 40px 20px;
  text-align: center;
  color: var(--text-muted);
`;

const FooterLinks = styled.div`
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 12px;

  a {
    color: var(--text-muted);
    font-size: 0.9rem;
  }

  a:hover {
    color: var(--primary);
  }
`;

const FooterBadges = styled.div`
  display: grid;
  gap: 16px;
  justify-items: center;
  margin: 20px 0 6px;
`;

const FooterBadgeGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
`;

const FooterBadge = styled.div`
  min-width: 104px;
  padding: 10px 14px;
  border-radius: 14px;
  border: 1px solid rgba(0, 212, 170, 0.18);
  background: rgba(255, 255, 255, 0.03);
  color: var(--text-light);
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.3;
`;

const FooterBadgeSubtitle = styled.div`
  color: var(--text-muted);
  font-size: 0.72rem;
  font-weight: 500;
  margin-top: 4px;
`;

interface PublicPageLayoutProps {
  title: string;
  subtitle: string;
  eyebrow?: string;
  children: React.ReactNode;
}

export const PublicPageLayout: React.FC<PublicPageLayoutProps> = ({
  title,
  subtitle,
  eyebrow = 'PontoFiel',
  children
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [siteSections, setSiteSections] = useState<any[]>([]);

  const loadSiteContent = async () => {
    try {
      const response = await api.get('/site-content');
      if (response.data?.success) {
        setSiteSections(response.data.data || []);
      }
    } catch (error) {
      console.error('Erro ao carregar conteudo do site:', error);
    }
  };

  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    return () => {
      document.head.removeChild(link);
    };
  }, []);

  useEffect(() => {
    loadSiteContent();
  }, []);

  const headerSection = siteSections.find(section => section.key === 'header');
  const footerSection = siteSections.find(section => section.key === 'footer');
  const headerNav = headerSection?.meta?.nav || [];
  const headerButtons = headerSection?.meta?.buttons || [];
  const logoUrl = headerSection?.meta?.logo_url || '/logo1.png';
  const footerLogoUrl = footerSection?.meta?.logo_url || '/logo1.png';
  const footerMeta = footerSection?.meta || {};
  const footerLinks = footerMeta.links || [];
  const paymentMethods = footerMeta.payment_methods || [];
  const trustBadges = footerMeta.trust_badges || [];
  const defaultNav = [
    { label: 'Planos', href: '/#planos' },
    { label: 'Blog', href: '/blog' },
    { label: 'Contato', href: '/contato' },
    { label: 'Termos', href: '/termos-de-uso' },
    { label: 'Privacidade', href: '/politica-de-privacidade' }
  ];
  const defaultFooterLinks = useMemo(() => ([
    { label: 'Termos de Uso', href: '/termos-de-uso' },
    { label: 'Politica de Privacidade', href: '/politica-de-privacidade' },
    { label: 'Contato', href: '/contato' },
    { label: 'Blog', href: '/blog' },
  ]), []);
  const defaultPaymentMethods = useMemo(() => ([
    { label: 'Visa' },
    { label: 'Mastercard' },
    { label: 'Elo' },
    { label: 'American Express' },
    { label: 'Hipercard' },
    { label: 'Pix' },
  ]), []);
  const defaultTrustBadges = useMemo(() => ([
    { label: 'Site blindado', subtitle: 'Monitoramento ativo' },
    { label: 'Compra segura', subtitle: 'Dados protegidos' },
  ]), []);
  const navItems = headerNav.length ? headerNav : defaultNav;
  const buttons = headerButtons.length ? headerButtons : [
    { label: 'Entrar', action: 'login' },
    { label: 'Criar Conta', action: 'register' }
  ];

  const resolveNavHref = (item: any) => {
    const href = item?.href || '';
    if (href.startsWith('#')) {
      return location.pathname === '/' ? href : `/${href}`;
    }
    return href || '/';
  };

  return (
    <>
      <PublicPageStyles />
      <div className="public-page">
        <Shell>
          <Glow $variant="top" />
          <Glow $variant="bottom" />
          <Header>
            <HeaderInner>
              <Logo onClick={() => navigate('/')}>
                <img src={logoUrl} alt="Ponto Fiel" />
              </Logo>
              <Nav>
                {navItems.map((item: any, index: number) => (
                  <a key={index} href={resolveNavHref(item)}>{item.label}</a>
                ))}
              </Nav>
              <HeaderActions>
                {buttons.map((button: any, index: number) => {
                  if (button.action === 'login') {
                    return (
                      <SecondaryButton key={index} onClick={() => navigate('/app/login')}>
                        {button.label}
                      </SecondaryButton>
                    );
                  }
                  if (button.action === 'register') {
                    return (
                      <PrimaryButton key={index} onClick={() => navigate('/register')}>
                        {button.label}
                      </PrimaryButton>
                    );
                  }
                  return (
                    <PrimaryButton key={index} onClick={() => navigate(button.href || '/register')}>
                      {button.label}
                    </PrimaryButton>
                  );
                })}
              </HeaderActions>
              <MobileToggle
                aria-label="Abrir menu"
                onClick={() => setIsMenuOpen((prev) => !prev)}
              >
                <span />
              </MobileToggle>
            </HeaderInner>
            <MobileMenu $open={isMenuOpen}>
              <MobileMenuInner>
                {navItems.map((item: any, index: number) => (
                  <a key={index} href={resolveNavHref(item)} onClick={() => setIsMenuOpen(false)}>
                    {item.label}
                  </a>
                ))}
                <MobileActions>
                  {buttons.map((button: any, index: number) => {
                    if (button.action === 'login') {
                      return (
                        <SecondaryButton
                          key={index}
                          onClick={() => { setIsMenuOpen(false); navigate('/app/login'); }}
                        >
                          {button.label}
                        </SecondaryButton>
                      );
                    }
                    if (button.action === 'register') {
                      return (
                        <PrimaryButton
                          key={index}
                          onClick={() => { setIsMenuOpen(false); navigate('/register'); }}
                        >
                          {button.label}
                        </PrimaryButton>
                      );
                    }
                    return (
                      <PrimaryButton
                        key={index}
                        onClick={() => { setIsMenuOpen(false); navigate(button.href || '/register'); }}
                      >
                        {button.label}
                      </PrimaryButton>
                    );
                  })}
                </MobileActions>
              </MobileMenuInner>
            </MobileMenu>
          </Header>

          <Hero>
            <HeroInner>
              <Eyebrow>{eyebrow}</Eyebrow>
              <Title>{title}</Title>
              <Subtitle>{subtitle}</Subtitle>
            </HeroInner>
          </Hero>

          <Content>{children}</Content>

          <Footer>
            <img src={footerLogoUrl} alt="Ponto Fiel" style={{ height: 62 }} />
            <div style={{ marginTop: 16 }}>
              {footerMeta.text || 'O sistema de fidelidade que sua empresa precisa.'}
            </div>
            <FooterBadges>
              <div>
                <div style={{ marginBottom: 10 }}>
                  {footerMeta.payment_note || 'Pagamentos processados com Mercado Pago.'}
                </div>
                <FooterBadgeGroup>
                  {(paymentMethods.length ? paymentMethods : defaultPaymentMethods).map((method: any, index: number) => (
                    <FooterBadge key={`payment-${index}`}>{method.label}</FooterBadge>
                  ))}
                </FooterBadgeGroup>
              </div>
              <FooterBadgeGroup>
                {(trustBadges.length ? trustBadges : defaultTrustBadges).map((badge: any, index: number) => (
                  <FooterBadge key={`trust-${index}`}>
                    {badge.label}
                    {badge.subtitle ? <FooterBadgeSubtitle>{badge.subtitle}</FooterBadgeSubtitle> : null}
                  </FooterBadge>
                ))}
              </FooterBadgeGroup>
            </FooterBadges>
            <FooterLinks>
              {(footerLinks.length ? footerLinks : defaultFooterLinks).map((link: any, index: number) => (
                <a key={index} href={link.href}>{link.label}</a>
              ))}
            </FooterLinks>
            <div style={{ marginTop: 14 }}>
              {footerMeta.copyright || `© ${new Date().getFullYear()} PontoFiel. Todos os direitos reservados.`}
            </div>
          </Footer>
        </Shell>
      </div>
    </>
  );
};
